<template>
	<div
		ref="element"
		class="call-draggable"
		:class="{'is-dragging':isDragging}"
		:draggable="draggable"
		@dragstart="dragStart"
		@dragend="dragEnd"
		@dragenter="dragEnter"
		@dragleave="dragLeave"
		@dragover="dragOver"
		@drop="dragDrop"
	>
		<div>
			<div class="dropbox-overlay" :class="{'visible':isOver}" :style="{'height': myHeight+'px','width': myWidth+'px'}"></div>
			<slot></slot>
		</div>
	</div>
</template>

<script setup lang="ts">
import CallDrag, { type QueueEnhancedCall, type QueueEnhancedMember, CallDragItemType, type CallDragItem, CallDragTargetType, type CallDragTarget } from '@/utilities/CallDrag'
import globalEvents from '@/utilities/GlobalEvents'
import { useEmployeesStore } from '@/stores/Employees'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import type { PbxAppQueuesAllItem } from '@/TcxApi/PbxApp'
import type Employee from '@/stores/Employee'
import { PbxCallState } from '@/TcxApi/PbxCommon'

const employees = useEmployeesStore()
const callDrag = CallDrag.instance

export interface Props {
	draggable: boolean
	target: boolean
	data?: CallDragItem | CallDragTarget
	/** The type of this drag item */
	type: CallDragItemType | CallDragTargetType
	/** The type of items that this target can receive */
	targetTypes?: CallDragItemType[]
}

const props = defineProps<Props>()

const isDragging = ref(false)
const isOver = ref(false)
const leaveTimer = ref<ReturnType<typeof setTimeout> | undefined>(undefined)
const lastEvent = ref<string | undefined>(undefined)
const myHeight = ref(0)
const myWidth = ref(0)
const dynamicTarget = ref<boolean | undefined>(undefined)
const element = ref<HTMLElement | undefined>(undefined)

const trueTarget = computed(() => {
	return dynamicTarget.value != undefined ? dynamicTarget.value : props.target
})

function dragStart(event: DragEvent) {
	if (props.draggable) {
		callDrag.dragItem = props.data as CallDragItem
		callDrag.dragItemType = props.type as CallDragItemType
		if (event.dataTransfer) {
			event.dataTransfer.effectAllowed = 'link'
		}
		isDragging.value = true
		lastEvent.value = 'dragStart'
		globalEvents.emit('CallDragStart', props)
	}
}

function dragEnd() {
	if (props.draggable || isDragging.value) {
		isDragging.value = false
		callDrag.dragItem = undefined
		callDrag.dragItemType = undefined
		callDrag.dragTarget = undefined
		callDrag.dragTargetType = undefined
		lastEvent.value = 'dragEnd'
		globalEvents.emit('CallDragEnd')
	}
}

function dragEnter(event: DragEvent) {
	if (!callDrag.dragItem || !trueTarget.value || callDrag.dragItem === props.data) {
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = 'none'
		}
		return
	}

	callDrag.dragTarget = props.data as CallDragTarget
	callDrag.dragTargetType = props.type as CallDragTargetType
	if (event.dataTransfer) {
		event.dataTransfer.dropEffect = 'move'
	}
	if (leaveTimer.value) {
		clearTimeout(leaveTimer.value)
		leaveTimer.value = undefined
	}
	if (callDrag.dragItem !== props.data) {
		isOver.value = true
		myHeight.value = element.value?.clientHeight ?? 0
		myWidth.value = element.value?.clientWidth ?? 0
	}
	lastEvent.value = 'dragEnter'
}

function dragOver(event: DragEvent) {
	event.preventDefault()
	if (!callDrag.dragItem || !trueTarget.value || callDrag.dragItem === props.data) {
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = 'none'
		}
		return
	}
	if (lastEvent.value === 'dragOver') { return }
	callDrag.dragTarget = props.data as CallDragTarget
	callDrag.dragTargetType = props.type as CallDragTargetType
	if (event.dataTransfer) {
		event.dataTransfer.dropEffect = 'move'
	}
	if (leaveTimer.value) {
		clearTimeout(leaveTimer.value)
		leaveTimer.value = undefined
	}
	if (callDrag.dragItem !== props.data) {
		isOver.value = true
		myHeight.value = element.value?.clientHeight ?? 0
		myWidth.value = element.value?.clientWidth ?? 0
	}
	lastEvent.value = 'dragOver'
}

function dragLeave(_event: DragEvent) {
	if (!callDrag.dragItem || !trueTarget.value || callDrag.dragItem === props.data) { return }
	callDrag.dragTarget = undefined
	callDrag.dragTargetType = undefined
	if (callDrag.dragItem !== props.data) {
		leaveTimer.value = setTimeout(() => {
			isOver.value = false
			leaveTimer.value = undefined
		}, 100)
	}
	lastEvent.value = 'dragLeave'
}

function dragDrop(event: DragEvent) {
	if (!callDrag.dragItem || !trueTarget.value || callDrag.dragItem === props.data) { return }
	event.preventDefault()
	isOver.value = false
	if (leaveTimer.value) {
		clearTimeout(leaveTimer.value)
		leaveTimer.value = undefined
	}
	lastEvent.value = 'dragDrop'
	callDrag.dragDropped()
}

function handleDragStartEvent(source: Props) {
	if (source !== props) {
		// Special cases

		// If source is Employee, and target is Employee, ensure source has a ringing call and we have pickup permission OR source has an ongoing call and we have steal permission
		if (source.type === CallDragItemType.Employee && ([CallDragTargetType.Employee, CallDragTargetType.MyCalls, CallDragTargetType.QueueCallers].includes(props.type as CallDragTargetType))) {
			const currentCall = (source.data as Employee).currentCall

			if (currentCall && [PbxCallState.CALL_START, PbxCallState.CALL_RING].includes(currentCall.state) && (source.data as Employee).currentCallExtension?.permissions.pickup) {
				dynamicTarget.value = true
			} else if (currentCall && [PbxCallState.CALL_ANSWER, PbxCallState.CALL_HOLD].includes(currentCall.state) && (source.data as Employee).currentCallExtension?.permissions.steal) {
				dynamicTarget.value = true
			} else {
				dynamicTarget.value = false
			}

		// If source is Employee and target is QueueMembers, ensure user has permission to add employee to queues
		} else if (source.type === CallDragItemType.Employee && props.type === CallDragTargetType.QueueMembers) {
			dynamicTarget.value = (source.data as Employee).permissions.queueMember && (props.data as PbxAppQueuesAllItem).dynamic

		// When adding a queue member to another queue, ensure it is another queue
		} else if (source.type === CallDragItemType.QueueMember && props.type === CallDragTargetType.QueueMembers) {
			dynamicTarget.value = employees.me?.permissions.queueMember && (props.data as PbxAppQueuesAllItem).dynamic && (props.data as PbxAppQueuesAllItem)._id !== (source.data as QueueEnhancedMember).queueId

		// When moving a call from one queue to another, ensure it is moved to another queue
		} else if (source.type === CallDragItemType.QueueCall && props.type === CallDragTargetType.QueueCallers) {
			dynamicTarget.value = (props.data as PbxAppQueuesAllItem)._id !== (source.data as QueueEnhancedCall).queueId
		} else if (Array.isArray(props.targetTypes)) {
			// Types: MyCalls, MyCallsCall, Employee, QueueCallers, QueueMembers, QueueCall, QueueMember
			// MyCalls accepts: Employee, QueueCall
			// MyCallsCall accepts: none - never does
			// Employee accepts: MyCallsCall, Employee (for picking up ringing/active call), QueueCall
			// QueueCallers accepts: MyCallsCall,
			// QueueMembers accepts: Employee,
			// QueueCall accepts: none - never does
			// QueueMember accepts: none - never does

			dynamicTarget.value = props.targetTypes && props.targetTypes.includes(source.type as CallDragItemType)
		} else {
			dynamicTarget.value = undefined
		}
	}
}

function handleDragEndEvent() {
	dynamicTarget.value = undefined
}

onMounted(() => {
	globalEvents.on('CallDragStart', handleDragStartEvent)
	globalEvents.on('CallDragEnd', handleDragEndEvent)
})

onUnmounted(() => {
	globalEvents.off('CallDragStart', handleDragStartEvent)
	globalEvents.off('CallDragEnd', handleDragEndEvent)
})
</script>

<style scoped>
	.is-dragging {
		opacity: 0.2;
	}
	.call-draggable {
		background: var(--bg-panel);
		border-radius: 5px;
	}
	.drop-target {
		transition: 500ms;
		border-radius: 5px 5px;
	}
	.drop-target-border {
		border: 1px dashed var(--border-color-dragdrop);
		background-color: var(--bg-color-dragdrop);
		margin-bottom: 10px;
	}
	.dropbox-overlay {
		opacity: 0;
		position: absolute;
		/* border: 3px solid var(--pn-telecom-1); */
		border-radius: 3px;
		color: var(--pn-telecom-1);
		display: block;
		z-index: 1000;
		background: var(--bg-color-dragdrop-dropbox);
		box-shadow: 0 0 5px 5px var(--pn-telecom-3);
		transition: opacity 600ms, max-height 1ms 601ms;
		max-height: 0;
	}
	.dropbox-overlay.visible {
		opacity: 1;
		transition: opacity 300ms, max-height 1ms;
		max-height: 1000px;
	}

</style>
